import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { notify } from '../utils/notify';

const FORM_INICIAL = { titulo: '', versao: '', descricao: '' };

const formatarData = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};

const linhas = (texto) => String(texto || '').split('\n').filter((l) => l.trim());

/**
 * Publicação e histórico dos avisos de atualização do sistema.
 * Restrito ao admin (backend devolve 403 para os demais).
 */
const AdminAtualizacoes = () => {
  const [form, setForm] = useState(FORM_INICIAL);
  const [enviando, setEnviando] = useState(false);
  const [itens, setItens] = useState([]);
  const [totalInternos, setTotalInternos] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [semAcesso, setSemAcesso] = useState(false);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE}/api/atualizacoes/`, authHeader());
      setItens(resp.data.atualizacoes || []);
      setTotalInternos(resp.data.total_internos || 0);
    } catch (err) {
      if (err.response?.status === 403) setSemAcesso(true);
      else notify.erro('Erro ao carregar as atualizações.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleChange = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const publicar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE}/api/atualizacoes/publicar/`, form, authHeader());
      notify.sucesso('Atualização publicada. Os operadores verão o aviso no próximo acesso.');
      setForm(FORM_INICIAL);
      carregar();
    } catch (err) {
      notify.erro(err.response?.data?.erro || 'Erro ao publicar a atualização.');
    } finally {
      setEnviando(false);
    }
  };

  const alternarAtivo = async (item) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_BASE}/api/atualizacoes/${item.id}/ativo/`,
        { ativo: !item.ativo },
        authHeader()
      );
      notify.sucesso(item.ativo ? 'Atualização despublicada.' : 'Atualização republicada.');
      carregar();
    } catch (err) {
      notify.erro(err.response?.data?.erro || 'Erro ao alterar a atualização.');
    }
  };

  if (semAcesso) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning mb-0">Acesso restrito ao administrador.</div>
      </div>
    );
  }

  const previa = linhas(form.descricao);

  return (
    <div className="container-fluid py-4">

      <div className="card mb-4">
        <div className="card-header bg-white">
          <h5 className="mb-0" style={{ color: '#212529' }}>
            <i className="fas fa-bullhorn me-2" style={{ color: '#c9a227' }}></i>
            Publicar atualização
          </h5>
          <small style={{ color: '#6c757d' }}>
            Cada operador interno verá este aviso uma vez e confirmará a leitura com OK
          </small>
        </div>
        <div className="card-body">
          <form onSubmit={publicar}>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label" htmlFor="atu-titulo">Título</label>
                <input
                  type="text"
                  id="atu-titulo"
                  className="form-control"
                  value={form.titulo}
                  maxLength={200}
                  onChange={(e) => handleChange('titulo', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="atu-versao">Versão (opcional)</label>
                <input
                  type="text"
                  id="atu-versao"
                  className="form-control"
                  value={form.versao}
                  maxLength={30}
                  placeholder="ex.: 2026.09"
                  onChange={(e) => handleChange('versao', e.target.value)}
                />
              </div>
              <div className="col-md-7">
                <label className="form-label" htmlFor="atu-descricao">Novidades (uma por linha)</label>
                <textarea
                  id="atu-descricao"
                  className="form-control"
                  rows={8}
                  value={form.descricao}
                  maxLength={5000}
                  onChange={(e) => handleChange('descricao', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-5">
                <label className="form-label">Prévia</label>
                <div className="border rounded p-3 h-100" style={{ backgroundColor: '#f8f9fa', minHeight: '12rem' }}>
                  <h6 className="fw-bold mb-2" style={{ color: '#212529' }}>{form.titulo || 'Título da atualização'}</h6>
                  {previa.length === 0 ? (
                    <small style={{ color: '#6c757d' }}>As linhas da descrição aparecem aqui como lista.</small>
                  ) : (
                    <ul className="mb-0 ps-3">
                      {previa.map((l, i) => <li key={i} className="mb-1">{l}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 d-flex justify-content-end">
              <button type="submit" className="btn btn-success" disabled={enviando} style={{ minWidth: '160px' }}>
                {enviando ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Publicando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2"></i>
                    Publicar
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header bg-white">
          <h5 className="mb-0" style={{ color: '#212529' }}>
            <i className="fas fa-clock-rotate-left me-2" style={{ color: '#212529' }}></i>
            Atualizações publicadas
          </h5>
          <small style={{ color: '#6c757d' }}>
            Leituras contam sobre {totalInternos} operador(es) interno(s) ativo(s)
          </small>
        </div>
        <div className="card-body p-0">
          {carregando ? (
            <div className="p-4 text-center" style={{ color: '#6c757d' }}>Carregando...</div>
          ) : itens.length === 0 ? (
            <div className="p-4 text-center" style={{ color: '#6c757d' }}>Nenhuma atualização publicada ainda.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '7rem' }}>Data</th>
                    <th style={{ width: '7rem' }}>Versão</th>
                    <th>Título</th>
                    <th style={{ width: '8rem' }} className="text-center">Leituras</th>
                    <th style={{ width: '8rem' }} className="text-center">Status</th>
                    <th style={{ width: '9rem' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((it) => (
                    <tr key={it.id} style={{ opacity: it.ativo ? 1 : 0.6 }}>
                      <td>{formatarData(it.publicado_em)}</td>
                      <td>{it.versao || '-'}</td>
                      <td>
                        <div className="fw-semibold">{it.titulo}</div>
                        <small style={{ color: '#6c757d' }}>{linhas(it.descricao).length} item(ns)</small>
                      </td>
                      <td className="text-center">{it.total_lidas} / {totalInternos}</td>
                      <td className="text-center">
                        <span className={`badge ${it.ativo ? 'bg-success' : 'bg-secondary'}`}>
                          {it.ativo ? 'Publicada' : 'Despublicada'}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className={`btn btn-sm ${it.ativo ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                          onClick={() => alternarAtivo(it)}
                        >
                          {it.ativo ? 'Despublicar' : 'Republicar'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAtualizacoes;
